import { cn } from '@/lib/utils';
import type { ShopperBasketsV2, ShopperOrders, ShopperProducts, ShopperSearch } from '@/scapi';
import type { ComponentProps } from 'react';
import CurrentPrice from './current-price';
import ListPrice from './list-price';
import PromoCallout from './promo-callout';
import { getPriceData } from './utils';
import { Badge } from '../ui/badge';
import { OmnibusPriceIndication, ProductTileUnitRow } from '../product-utils';
import type { OmnibusPriceDisplay } from '@/lib/omnibus';

type Product =
    | ShopperProducts.schemas['Product']
    | (ShopperBasketsV2.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | (ShopperOrders.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
    | ShopperSearch.schemas['ProductSearchHit'];

interface ProductPriceProps {
    labelForA11y?: string;
    product: Product;
    currency: string;
    quantity?: number;
    currentPriceProps?: Omit<ComponentProps<typeof CurrentPrice>, 'price' | 'currency' | 'labelForA11y'>;
    listPriceProps?: Omit<ComponentProps<typeof ListPrice>, 'price' | 'currency' | 'labelForA11y'>;
    promoCalloutProps?: Omit<ComponentProps<typeof PromoCallout>, 'product'>;
    type?: 'unit' | 'total';
    className?: string;
    /** Hide promotional callout text (e.g. in modal/edit mode) */
    hidePromo?: boolean;
    /** Show only the current price without "From" prefix, list price, or promo (e.g. in modal/edit mode) */
    currentPriceOnly?: boolean;
    showPriceWeightUnit?: boolean;
    showPriceWeightBadge?: boolean;
    pricePct?: number;
    unitRow?: { weightText: string; perUnitText?: string };
    highlightOnSaleCurrentPrice?: boolean;
    invertTextColor?: boolean;
    /**
     * Omnibus price disclosure (`useProductPriceWeightDisplay().omnibus`, EATWS-472). When it carries a
     * previous price it replaces the struck-through list price and `pricePct` is expected to be
     * computed against it.
     */
    omnibus?: OmnibusPriceDisplay | null;
}

/**
 * @param product - product object containing price information
 * // If a product is a set,
 *      on PDP, Show From X where X is the lowest of its children and
 *          the set children will have it own price as From X (cross) Y
 * // if a product is a master
 *      on PDP, show From X (cross) Y , the X and Y are
 *          current and list price of variant that has the lowest price (including promotionalPrice)
 * // if a standard/bundle
 *      show price on PDP as X (cross) Y
 * @param currency - currency
 * @param quantity - quantity to take into the account for price display
 * @param currentPriceProps - extra props to be passing to CurrentPrice component
 * @param listPriceProps - extra props to be passing to ListPrice component
 * @param promoCalloutProps - extra props to be passing to PromoCallout component (className overrides default)
 * @param type - type of price to display. 'unit' for unit price, 'total' for total price (unit price * quantity).
 * @param labelForA11y - label to be used for a11y
 */
export default function ProductPrice({
    labelForA11y,
    product,
    currency,
    quantity = 1,
    type = 'total',
    currentPriceProps = {},
    listPriceProps = {},
    promoCalloutProps = {},
    className,
    hidePromo = false,
    currentPriceOnly = false,
    showPriceWeightUnit = false,
    showPriceWeightBadge = false,
    pricePct = 0,
    unitRow = { weightText: '', perUnitText: '' },
    highlightOnSaleCurrentPrice = false,
    omnibus = null,
}: ProductPriceProps) {
    const priceData = getPriceData(product, { quantity });
    const { listPrice, currentPrice, isASet, isMaster, isOnSale, isRange, maxPrice } = priceData;

    // const { localeRef } = useCurrentSiteAndLocaleRef();

    // Show strikethrough (list price) only for single price, not when displaying price range (min – max)
    const showPriceRange = isRange && maxPrice != null && maxPrice > currentPrice;
    const hasOmnibusReference = omnibus?.priorBestPrice != null;
    const isOmnibusNonReduction = omnibus?.priorBestPrice != null && omnibus.priorBestPrice <= currentPrice;
    const hasOmnibusPreviousPrice = Boolean(omnibus?.priorFormatted);
    const showListPrice = isOnSale && listPrice && !showPriceRange && !hasOmnibusReference;

    const omnibusNode =
        omnibus && !currentPriceOnly ? <OmnibusPriceIndication indication={omnibus} className="mt-1" /> : null;

    const renderPriceWeightSupplement = () => {
        if (!showPriceWeightUnit && !showPriceWeightBadge) return null;
        const perUnitText = unitRow.perUnitText || undefined;
        return (
            <div
                className="content-start flex w-full min-w-0 flex-wrap items-start gap-4"
                data-testid="product-pdp-price-weight-supplement">
                <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col gap-1">
                    {showPriceWeightUnit ? (
                        <ProductTileUnitRow weightText={unitRow.weightText} perUnitText={perUnitText} />
                    ) : null}
                </div>
                {showPriceWeightBadge && pricePct > 0 && !isOmnibusNonReduction ? (
                    <div className="shrink-0 self-start">
                        <Badge hierarchy="primary" schema="danger" className="shrink-0">
                            -{pricePct}%
                        </Badge>
                    </div>
                ) : null}
            </div>
        );
    };

    if (currentPriceOnly) {
        return (
            <div className={cn('flex flex-col gap-3', className)}>
                <div className="flex items-center gap-2">
                    <CurrentPrice
                        labelForA11y={labelForA11y}
                        price={type === 'unit' ? currentPrice : currentPrice * quantity}
                        as="span"
                        currency={currency}
                        isRange={false}
                        {...currentPriceProps}
                    />
                </div>
                {renderPriceWeightSupplement()}
            </div>
        );
    }

    const renderCurrentPrice = (flag: boolean) => (
        <CurrentPrice
            labelForA11y={labelForA11y}
            price={type === 'unit' ? currentPrice : currentPrice * quantity}
            as="span"
            currency={currency}
            isRange={flag}
            maxPrice={flag && maxPrice != null ? (type === 'unit' ? maxPrice : maxPrice * quantity) : undefined}
            highlightOnSaleCurrentPrice={highlightOnSaleCurrentPrice && !isOmnibusNonReduction}
            {...currentPriceProps}
        />
    );

    const renderListPrice = (flag: boolean) => {
        const showStrikethrough = Boolean(listPrice && listPrice !== currentPrice && !hasOmnibusReference);
        const showBadge =
            !hidePromo && !isOmnibusNonReduction && pricePct > 0 && (showStrikethrough || hasOmnibusPreviousPrice);
        if (!showStrikethrough && !showBadge) return null;
        return (
            <div className={cn('items-center gap-4 flex', className)}>
                {showStrikethrough && listPrice ? (
                    <ListPrice
                        labelForA11y={labelForA11y}
                        currency={currency}
                        price={type === 'unit' ? listPrice : listPrice * quantity}
                        isRange={flag}
                        {...listPriceProps}
                    />
                ) : null}
                {showBadge && (
                    <Badge hierarchy="primary" schema="danger" className="shrink-0">
                        -{pricePct}%
                    </Badge>
                )}
            </div>
        );
    };

    const renderPriceSet = (flag: boolean) => (
        <>
            {renderCurrentPrice(flag)} {(showListPrice || hasOmnibusPreviousPrice) && renderListPrice(flag)}
        </>
    );

    // Points badge removed: product.price.points remains in locales (unused; keep for possible restore).

    if (isASet) {
        return (
            <>
                <div className={cn('items-center gap-2', className)}>{renderCurrentPrice(true)}</div>
                {!hidePromo && !isOmnibusNonReduction && (
                    <PromoCallout
                        product={product}
                        {...promoCalloutProps}
                        className={cn(className, promoCalloutProps?.className)}
                    />
                )}
                {renderPriceWeightSupplement()}
                {omnibusNode}
            </>
        );
    }

    if (isMaster) {
        return (
            <>
                <div className={cn('items-center gap-2', className)}>{renderPriceSet(isRange ?? false)}</div>
                {!hidePromo && !isOmnibusNonReduction && (
                    <PromoCallout
                        product={product}
                        {...promoCalloutProps}
                        className={cn(className, promoCalloutProps?.className)}
                    />
                )}
                {renderPriceWeightSupplement()}
                {omnibusNode}
            </>
        );
    }

    return (
        <div className="flex-1 flex flex-col">
            <div className="flex justify-between gap-2 items-end">
                <div className={cn('flex flex-col', className)}>
                    {renderListPrice(true)}
                    {renderCurrentPrice(false)}
                </div>
                <div className="pb-1">{renderPriceWeightSupplement()}</div>
            </div>
            {omnibusNode}
        </div>
    );
}
